<script setup lang="ts">
import { useAppointmentActions } from '@/composables/Appointment/useAppointmentActions'
import AppointmentRecentDialog from './AppointmentRecentDialog.vue'
import AppointmentPostponeDialog from './AppointmentPostponeDialog.vue'
import { showConfirmDialog, showNotify } from 'vant'
import { ref, onMounted } from 'vue'
import { useAppointmentRecents } from '@/composables/Appointment/useAppointmentRecents'
import { useAppointmentRepository } from '@/repositories/appointmentRepo'
import { AppointmentEventType } from '@/type/Appointment'
import { useAppointmentRecentsStore } from '@/stores/AppointmentStore'
import { useTranslation } from 'i18next-vue'
import { useAppStore } from '@/stores/AppStore'
import { getBranchDisplayNamei18n } from '@/utils/branch'

interface Props {
  limit?: number
}
const props = defineProps<Props>()
interface EmitEvents {
  (e: 'postpone', data: { uuid: string }): void
  (e: 'cancel', data: { uuid: string }): void
  (e: 'refresh'): void
}
const emit = defineEmits<EmitEvents>()
const appointmentRepo = useAppointmentRepository()
const appointmentRecentsStore = useAppointmentRecentsStore()
const { cancelAppointment } = useAppointmentActions()
const { t } = useTranslation()
const appStore = useAppStore()
const isLoading = ref(false)
const isError = ref(false)

const { DEFAULT_LIMIT, fetchLimitedRecentAppointments } = useAppointmentRecents()
const isMoreThanLimit = ref(false)
const getLimitedRecentAppointments = async () => {
  try {
    isLoading.value = true
    const { data } = await fetchLimitedRecentAppointments(props.limit)
    appointmentRecentsStore.setRecentAppointments(data.results)
    appointmentRecentsStore.setAppointmentCount(data.count)
    if (data.count > (props.limit || DEFAULT_LIMIT)) {
      isMoreThanLimit.value = true
    }
  } catch (error) {
    showNotify({
      type: 'danger',
      message: t('appointment.error.fetch_upcoming'),
      duration: 5000,
    })
    isError.value = true
  } finally {
    isLoading.value = false
  }
}

const formatDate = (dateString: string) => {
  // Expecting format like "2025-10-20" and converting to "20/10/25"
  const [year, month, day] = dateString.split('-')
  return `${day}/${month}/${year.slice(-2)}`
}
const formatTime = (timeString: string) => {
  // Expecting format like "15:20:00" and converting to "15.20"
  return timeString.slice(0, 5).replace(':', '.')
}

const onCancelAppointment = async (
  appointment_uuid: string,
  branch_name: string,
  appoint_date: string,
  appoint_time: string,
) => {
  try {
    // confirm dialog
    // ถ้า confirm ก็จะเรียก cancelAppointment
    await showConfirmDialog({
      title: t('appointment.cancel_confirm_title'),
      message: t('appointment.cancel_confirm_message', {
        branch: branch_name,
        date: appoint_date,
        time: appoint_time,
      }),
      confirmButtonText: t('appointment.yes'),
      confirmButtonColor: 'green',
      cancelButtonText: t('appointment.no'),
      cancelButtonColor: 'red',
      overlay: true,
    })
    const result = await cancelAppointment(appointment_uuid)
    if (result) {
      appointmentRepo.createAppointmentTrickingEvent(AppointmentEventType.CANCEL_APPOINTMENT)
      getLimitedRecentAppointments()
      dialog_ref.value?.fetchAppointments() // update in dialog
    }
  } catch (error) {
    // error จาก API ถูก catch ไว้แล้วใน useAppointmentActions
    // แต่ถ้า ยกเลิก ก็ไม่ต้องทำอะไร
    return
  }
}

// dialog
const dialog_ref = ref<InstanceType<typeof AppointmentRecentDialog> | null>(null)
const dialog = ref(false)
const postpone_dialog = ref(false)
const selectedAppointmentUuid = ref('')

const openDialog = (uuid?: string) => {
  dialog.value = true
  if (uuid) {
    selectedAppointmentUuid.value = uuid
  }
}
const closeDialog = () => {
  dialog.value = false
}
const openPostponeDialog = (uuid: string) => {
  selectedAppointmentUuid.value = uuid
  postpone_dialog.value = true
}
const closePostponeDialog = () => {
  postpone_dialog.value = false
  selectedAppointmentUuid.value = ''
}
const onPostponeSuccess = async () => {
  await getLimitedRecentAppointments()
  dialog_ref.value?.fetchAppointments() // update in dialog
  emit('refresh')
}

defineExpose({
  getLimitedRecentAppointments,
})

onMounted(async () => {
  await getLimitedRecentAppointments()
})
</script>

<template>
  <div class="tw-w-full">
    <h2 class="tw-text-[#2f4b76] tw-text-2xl tw-font-bold tw-mb-2">
      {{ t('appointment.upcoming') }}
      <span v-if="appointmentRecentsStore.appointmentCount"> ({{ appointmentRecentsStore.appointmentCount }}) </span>
    </h2>

    <div v-if="isLoading">
      <van-loading class="tw-flex tw-justify-center tw-items-center tw-h-full" />
    </div>

    <div v-else-if="isError">
      <p class="tw-text-red-500 tw-text-lg tw-text-center tw-my-2">{{ t('appointment.error.load_upcoming') }}</p>
    </div>

    <div v-else-if="appointmentRecentsStore.recentAppointments.length === 0">
      <p class="tw-text-gray-500 tw-text-lg tw-text-center tw-my-2">{{ t('appointment.no_appointment') }}</p>
    </div>

    <!-- Appointment Cards -->
    <div v-else class="tw-flex tw-gap-3 tw-overflow-x-auto">
      <div
        v-for="appointment in appointmentRecentsStore.recentAppointments"
        :key="appointment.uuid"
        class="tw-min-w-[290px] tw-bg-white tw-rounded-2xl tw-border-2 tw-border-[#c8c9cc] tw-p-2"
      >
        <div class="tw-flex tw-justify-between tw-items-center">
          <div class="tw-flex-1">
            <div class="tw-text-gray-700 tw-text-lg tw-mb-1">
              <span class="tw-font-medium">{{ t('appointment.branch_label') }}</span>
              {{ getBranchDisplayNamei18n(appointment, appStore.language) }}
            </div>
            <div class="tw-text-gray-700 tw-text-lg tw-mb-1">
              <span class="tw-font-medium">{{ t('appointment.date_label') }}</span>
              {{ formatDate(appointment.appoint_date) }}
            </div>
            <div class="tw-text-gray-700 tw-text-lg">
              <span class="tw-font-medium">{{ t('appointment.time_label') }}</span>
              {{ formatTime(appointment.appoint_time) }}
            </div>
          </div>
          <div class="tw-mx-2 tw-flex tw-flex-col tw-gap-2">
            <v-btn
              color="#FDB94E"
              class="tw-px-[20px] tw-rounded-full tw-text-sm tw-whitespace-nowrap"
              size="small"
              @click="openPostponeDialog(appointment.uuid)"
            >
              {{ t('appointment.postpone') }}
            </v-btn>
            <v-btn
              color="#E74C3C"
              class="tw-px-[20px] tw-text-white tw-rounded-full tw-text-sm tw-whitespace-nowrap"
              size="small"
              @click="
                onCancelAppointment(
                  appointment.uuid,
                  getBranchDisplayNamei18n(appointment, appStore.language),
                  formatDate(appointment.appoint_date),
                  formatTime(appointment.appoint_time),
                )
              "
            >
              {{ t('cancel') }}
            </v-btn>
          </div>
        </div>
      </div>

      <div v-if="isMoreThanLimit" class="tw-flex tw-items-center tw-justify-center">
        <v-btn
          color="#3498DB"
          class="tw-px-[20px] tw-text-white tw-rounded-full tw-text-sm tw-whitespace-nowrap"
          size="small"
          @click="openDialog()"
        >
          {{ t('appointment.view_all') }}
        </v-btn>
      </div>
    </div>

    <AppointmentRecentDialog
      ref="dialog_ref"
      :show="dialog"
      @on-close="closeDialog"
      @on-cancel="onCancelAppointment"
      @on-postpone="openPostponeDialog"
    />

    <AppointmentPostponeDialog
      :show="postpone_dialog"
      :appointment-uuid="selectedAppointmentUuid"
      @close="closePostponeDialog"
      @success="onPostponeSuccess"
    />
  </div>
</template>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.tw-overflow-x-auto::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.tw-overflow-x-auto {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
